// FnolNotifier represents the source of the FNOL notification.
export type FnolNotifier = 'vet' | 'customer' | 'broker' | 'other';

// FnolNotificationMedium represents how the FNOL notification was received.
export type FnolNotificationMedium = 'fnol' | 'email' | 'letter' | 'other';

// DiagnosticCategory represents the category of a diagnosis.
export type DiagnosticCategory = 'NOT_PROVIDED' | 'UNKNOWN' | 'ACCIDENT' | 'ILLNESS' | 'PREVENTION';

// ClaimType represents the main type of an insurance claim.
export type ClaimType = 'none' | 'krankheit' | 'notfallbetreuung' | 'praevention' | 'unfall';

// ClaimSubtype represents the subtype of an insurance claim.
export type ClaimSubtype =
  | 'none'
  | 'allgemeine_tieraerztliche_behandlung'
  | 'komplementaermedizinische_behandlung'
  | 'krankenhausaufenthalt_des_besitzers'
  | 'medizinalfutter'
  | 'notfallbehandlung'
  | 'praeventive_behandlungen'
  | 'verhaltensmedizinische_behandlung'
  | 'zahnbehandlung'
  | 'fortgeschrittene_behandlung';

// ClaimCause represents the specific cause of an insurance claim.
export type ClaimCause =
  | 'none' | 'akupunktur' | 'behandlung_ausserhalb_der_sprechzeiten' | 'behandlung_waehrend_der_sprechzeiten'
  | 'floh_und_wurmkuren' | 'hausbesuch_durch_tierarzt' | 'homoeopathische_pflanzliche_arzneimittel' | 'impfungen'
  | 'jaehrliche_tieraerztliche_kontrolle' | 'kastration' | 'notfallmaessiger_aufenthalt' | 'osteopathie'
  | 'physiotherapie' | 'hydrotherapie' | 'lasertherapie' | 'verhaltensstoerung' | 'verschreibungspflichtiges_futter'
  | 'zahnerkrankung' | 'zahnsteinentfernung' | 'zahnunfall' | 'stammzellenbehandlung' | 'gentherapie'
  | 'transplantationschirurgie' | 'prothesen' | 'abszess' | 'hautwunde' | 'autounfall' | 'vergiftung' | 'fremdkoerper'
  | 'knochenbruch' | 'krallenverletzung' | 'kreuzbandriss' | 'kippfenster' | 'sturz' | 'muskel_sehnenzerrung'
  | 'augenverletzung' | 'zahnverletzung' | 'bissverletzung' | 'blutung' | 'allergische_reaktion' | 'magendrehung'
  | 'verbrennung' | 'hitzschlag' | 'ohrentzuendung' | 'hauterkrankung' | 'schnupfen' | 'husten' | 'durchfall'
  | 'nierenerkrankung' | 'verstopfung' | 'blasenentzuendung' | 'urinabsatzbeschwerden' | 'fieber_unbekannter_ursache'
  | 'infektionskrankheit' | 'appetitlosigkeit' | 'magendarmentzuendung' | 'herzerkrankung' | 'lungenerkrankung'
  | 'rueckenschmerzen' | 'lahmheit' | 'augenerkrankung' | 'bauchspeicheldruesenentzuendung' | 'diabetes'
  | 'schilddruesenerkrankung' | 'analbeutelverstopfung' | 'laehmung' | 'hormonelle_erkrankung' | 'nervenerkrankung'
  | 'bandscheibenvorfall' | 'allergie' | 'chirurgie' | 'krebs';

// ReasonNotCovered explains why a claim or position might not be covered.
export type ReasonNotCovered =
  | ''
  | 'within-deductible'
  | 'within-waiting-period'
  | 'no-coverage-according-to-gtc'
  | 'damage-before-contract-start'
  | 'coverage-interrupt'
  | 'division-not-insured'
  | 'additional-coverage-not-insured'
  | 'others';

// FormalCoverageCheckValidationStatus indicates the status of formal checks.
export type FormalCoverageCheckValidationStatus = 'not_necessary' | 'open' | 'performed';

// --- Interface Definitions ---

// S3Document represents a reference to a document stored in S3.
export interface S3Document {
  s3Id: string; // prepend this with the ID of the FNOL Request and a slash, i.e. <ID_FNOL_REQUEST>/<S3ID>
}

// DiagnosisData holds information about a diagnosis provided during FNOL.
export interface DiagnosisData {
  id: string;
  category: DiagnosticCategory; // Validation like "required,oneof=..." needs to be handled in application logic
}

// InvoiceMetadata contains metadata extracted from an invoice document.
export interface InvoiceMetadata {
  issuerName: string;
  issuerAddress: string;
  recipientName: string;
  recipientAddress: string;
  invoiceNumber: string;
  invoiceDate: string;
  language: string;
}

// Classification holds the insurance classification details for a position.
export interface Classification {
  type: ClaimType;
  subtype: ClaimSubtype;
  cause: ClaimCause;
  clusterAssignment: string;
  followUpTreatment: boolean;
  followUpTreatmentClaim: string;
  followUpTreatmentCluster: string;
  preexistingCondition: boolean;
  emergencyAbroad: boolean;
  chronicDisease: boolean;
  obligationsFulfilled: boolean;
  furtherClarificationComment: string;
  covered: boolean;
  reasonNotCovered: ReasonNotCovered;
  courseOfEvents: string;
  genericComment: string;
  preexistingConditionsUsedUpDeductible: number | null;
  accidentDate: string | null;
  ignoreWaitingPeriod: boolean;
}

// Position represents a single line item on an invoice.
export interface Position {
  name: string;
  quantity: string; // Kept as string as in Go struct
  vatPercentage: string; // Kept as string as in Go struct
  amount: number;
  classification: Classification | null;
}

// TreatmentComment holds comments added to a treatment.
export interface TreatmentComment {
  fieldName: string;
  comment: string;
  addedBy: string;
}

// PetInfo represents information about a pet.
export interface FnolPetInfo {
  pet_name: string;
  pet_species: string;
  pet_gender: string;
  breed_type: boolean; // Assuming "mischrasse_haustier" is a boolean
  breed: string;
  birthdate: string; // ISO date string format recommended
  neutered: boolean;
}

// Customer represents information about the policyholder.
export interface Customer {
  firstname: string;
  lastname: string;
  email: string;
  street: string;
  houseNumber: string;
  zip: string;
  city: string;
  language: string;
  phoneNumber: string;
  ibanPresent: boolean;
}

// InsuranceCoverage represents information about a specific insurance coverage.
export interface InsuranceCoverage {
  coverage_selected: boolean;
  sublimit_sum?: number;
  sublimit_percentage?: number;
  used_up_sum_insured?: number; // -1 if unknown, can be represented by null or a specific value
}

// ClaimInfo represents information about a claim associated with a contract.
export interface ClaimInfo {
  identifier: string;
  claim_date: string; // ISO date string format recommended
  paid_amount: number;
  expense: number;
  used_franchsie: number; // Note: This represents the latest state of the used franchise, not the snapshot at the date of the claim
  status: string;
  closing_reason: string;
  closing_date: string; // ISO date string format recommended
  type: string; // illness/accident/prevention (claims category in ppt)
  sub_type: string; // e.g. dental treatment, vaccination (claims type in ppt)
  cause: string;
  insured_event: boolean; // Corrected typo from Go struct (InusredEvent -> insured_event)
  reason_why_not_insured: string;
  deduction_yearly_franchise: number;
  deduction_percentage_deductible: number;
  deduction_deductible: number;
  benefit_amount: number; // ignore, can be inconsistent
  precedent_claim_id: string;
  deduction_non_covered_positions?: number;
  deduction_discount?: number;
  deduction_others?: number;
}

// Interface for the anonymous struct within Contract for PreexistingConditionsCoverage
export interface PreexistingConditionsCoverageInfo {
  yearly_net_premium: number;
  sum_insured_current_year: number;
  preexisting_conditions: boolean;
  used_up_sum_insured: number;
}

// Interface for the anonymous struct within Contract for WorldwideProtectionCoverage
export interface WorldwideProtectionCoverageInfo {
  yearly_net_premium: number;
  sum_insured: number;
  worldwide_protection: boolean;
  used_up_sum_insured: number;
}


// Contract represents information about an insurance contract.
export interface Contract extends FnolPetInfo, Customer {
  full_contract_number: string;
  full_contract_number_year_adjusted: string; // this number contains an indicator suffix which tells what year of the contract duration the treatment containing this contract was in
  contract_number: string;
  customer_id: string;
  status: string;
  start_date: string; // ISO date string format recommended
  end_date: string; // when the contract would end without renewal; ISO date string format recommended
  cancellation_date: string; // when the contract ends; ISO date string format recommended
  date_of_wish_to_cancel: string; // when the customer said they want to cancel; ISO date string format recommended
  gic_version: string;
  yearly_gross_premium: number; // incl. taxes
  yearly_net_premium: number; // excl. taxes
  payment_interval: string; // monatlich/jährlich (monthly/yearly)
  taxes: number;
  product_bundle: string; // S/M/L
  yearly_net_premium_base: number;
  annual_franchise: number;
  used_up_annual_franchise: number; // -1 if unknown, can be represented by null or a specific value
  deductible_percentage_until_9_years: number;
  deductible_percentage_after_9_years: number;
  overall_sum_insured: number;
  used_up_sum_insured: number; // -1 if unknown, can be represented by null or a specific value

  accident_and_illness_coverage: InsuranceCoverage;
  illness_advanced_treatment_coverage: InsuranceCoverage;
  illness_complementary_treatment_coverage: InsuranceCoverage;
  illness_behavioural_treatment_coverage: InsuranceCoverage;
  accident_dental_treatment_coverage: InsuranceCoverage;
  illness_dental_treatment_coverage: InsuranceCoverage;
  emergency_minding_coverage: InsuranceCoverage;
  emergency_treatment_coverage: InsuranceCoverage;
  medical_food_coverage: InsuranceCoverage;
  preventive_care_coverage: InsuranceCoverage;

  preexisting_conditions_coverage: PreexistingConditionsCoverageInfo;
  worldwide_protection_coverage: WorldwideProtectionCoverageInfo;

  claims: ClaimInfo[];
}

// Treatment represents a set of procedures performed on a specific date for a pet.
export interface Treatment {
  petName: string;
  date: string; // ISO date string format recommended
  diagnosis: string;
  positions: Position[];
  subTotal: number;
  contract: Contract | null; // Mapped from *Contract
  contractNumber: string; // not the full number, just the first part
  comments: TreatmentComment[];
  invoiceRecipientValidationStatus: FormalCoverageCheckValidationStatus;
  petNameValidationStatus: FormalCoverageCheckValidationStatus;
  hasOverdueInvoices: boolean;
  dateOverdueInvoicesChecked: string; // ISO date string format recommended
}

// VatAndDiscountCalculationInformation holds details for applying VAT and discounts.
export interface VatAndDiscountCalculationInformation {
  vatContainedInPositions: boolean;
  totalInclVat: number;
  totalExclVat: number;
  vatAmountToDistribute: number;
  vatPercentageToDistribute: number;
  applicableDiscountAmount: number;
  forexFactor: number | null; // Mapped from *float64
}

// ParsedInvoiceData contains all data extracted and calculated from an invoice.
export interface ParsedInvoiceData {
  invoiceMetadata: InvoiceMetadata;
  treatments: Treatment[];
  sumWithoutVat: number;
  sumWithVat: number;
  calculatedSum: number;
  calculatedSumAfterDiscount: number; // Corrected typo from Go struct
  calculationInformation: VatAndDiscountCalculationInformation | null; // Mapped from *VatAndDiscountCalculationInformation
}

// FnolRequest is the main structure holding all information related to a First Notice of Loss request.
// Attributes removed as per request: Tasks, OcrExtractionID, MfvClaims, MfvReserveClaimContractID, MfvReserveClaimID, InitialReserveData, ParentRequestID, ClonedRequestIDs
export interface FnolRequest {
  id: string;
  dummy_gsi: string;
  created_at?: string; // ISO date string format recommended
  updated_at?: string; // ISO date string format recommended
  notifier: FnolNotifier; // One of "vet/customer/broker/other"
  notificationMedium: FnolNotificationMedium; // One of "email/fnol/postmail/..."
  notificationDate: string; // ISO date string format recommended
  processingStatus: number; // Enum-like status code
  processingStatusAgg: string;
  processingAssignee: string;
  labels: string[];
  priority: number;
  invoiceDocument: S3Document | null; // Mapped from *S3Document
  additionalDocuments: S3Document[] | null; // Mapped from *[]S3Document
  parsedInvoiceData: ParsedInvoiceData | null; // Mapped from *ParsedInvoiceData
  // Below is the data entered in the FNOL frontend.
  fnolFirstName: string;
  fnolLastName: string;
  fnolEmail: string;
  fnolPhoneNumber: string;
  fnolPreferredChannel: string;
  fnolLang: string; // Mapped from fnolLang
  fnolIban: string; // Mapped from fnolIban
  fnolPetName: string;
  fnolConditionPreviouslyTreated: boolean;
  fnolDateConditionLastTreated: string; // ISO date string format recommended
  fnolDateConditionFirstTreated: string; // ISO date string format recommended
  fnolDateConditionNoticed: string; // ISO date string format recommended
  fnolDateOfTreatment: string; // ISO date string format recommended
  fnolTreatedInSwitzerland: boolean;
  fnolInvoicedAmount: number;
  fnolTreatmentFinished: boolean;
  fnolThirdPartyFault: boolean;
  fnolAdditionalInformation: string;
  fnolDiagnoses: DiagnosisData[];
  fnolWhitelabelFlavor: string;
  selectedContractIds: string[];
  appliedDiscount: number;
  expected_date?: Date;
}
